import { evaluateGuess } from './evaluate';
import { maxGuessesFor } from './lengths';
import type { EvaluatedGuess, GameMode, GameStatus } from './types';

export interface GameState {
  answer: string;
  mode: GameMode;
  guesses: EvaluatedGuess[];
  status: GameStatus;
  hintsUsed: number;
  /** Answer positions revealed by hints (0-based). */
  revealedHints: number[];
}

export type SubmitError = 'invalid-word' | 'bad-length' | 'game-over';
export type SubmitResult =
  | { ok: true; state: GameState }
  | { ok: false; error: SubmitError };

export function createGame(answer: string, mode: GameMode): GameState {
  return {
    answer: answer.toLowerCase(),
    mode,
    guesses: [],
    status: 'playing',
    hintsUsed: 0,
    revealedHints: [],
  };
}

/** Pure: returns a new state; rejected submissions never consume a guess. */
export function submitGuess(
  state: GameState,
  word: string,
  isValid: (w: string) => boolean,
): SubmitResult {
  if (state.status !== 'playing') return { ok: false, error: 'game-over' };
  const w = word.toLowerCase();
  // The board is as wide as the answer (spec §10.1); anything else is a
  // half-typed row, not a word we can evaluate.
  if (w.length !== state.answer.length) return { ok: false, error: 'bad-length' };
  if (!isValid(w)) return { ok: false, error: 'invalid-word' };

  const guesses = [...state.guesses, { word: w, tiles: evaluateGuess(w, state.answer) }];
  const status: GameStatus =
    w === state.answer ? 'won'
      : guesses.length >= maxGuessesFor(state.answer) ? 'lost'
        : 'playing';
  return { ok: true, state: { ...state, guesses, status } };
}
